/**
 * Unpaid property taxes on a unit: what it is allowed to say.
 *
 * The record is the county Tax Collector's own list of delinquent real
 * property taxes, exported by hand each month (Sarasota and Collier since
 * 29 Sep 2026). So every sentence names the list and its date. It says the
 * taxes were unpaid on that day — a unit can be paid off the day after — and
 * nothing about why, or about the owner.
 *
 * **Absence is silence.** A unit not on the list carries no years and no
 * amount, which is "not listed on the list we hold", never "paid": the list
 * is only held for some counties.
 */

import type { Unit } from "../api/types";
import { money } from "./format";
import { formatDay } from "./sirsAssessment";

type Taxes = Pick<Unit, "tax_years" | "tax_owed" | "tax_as_of">;

/** Whether the Tax Collector's list names this unit. */
export function hasUnpaidTaxes(unit: Taxes): boolean {
  return (unit.tax_years ?? []).length > 0;
}

/** "2025, 2024 and 2023", newest first. */
export function taxYearsLabel(unit: Taxes): string {
  const years = (unit.tax_years ?? []).map(String);
  if (years.length <= 1) return years.join("");
  return `${years.slice(0, -1).join(", ")} and ${years[years.length - 1]}`;
}

/**
 * "Taxes unpaid for 2025 and 2024 · $4,693", for a row or a popover line.
 * "" when the unit is not on the list.
 */
export function unpaidTaxesLabel(unit: Taxes): string {
  if (!hasUnpaidTaxes(unit)) return "";
  const owed = unit.tax_owed !== null && unit.tax_owed !== undefined
    ? ` · ${money(unit.tax_owed)}` : "";
  return `Taxes unpaid for ${taxYearsLabel(unit)}${owed}`;
}

/**
 * "2025 and 2024 · $4,693 · listed 29 Sep 2026", for the score popover, where
 * the signal's own label already says "Taxes unpaid". "" when not listed.
 */
export function unpaidTaxesDetail(unit: Taxes): string {
  if (!hasUnpaidTaxes(unit)) return "";
  const when = formatDay(unit.tax_as_of);
  return [
    taxYearsLabel(unit),
    unit.tax_owed !== null && unit.tax_owed !== undefined ? money(unit.tax_owed) : "",
    when ? `listed ${when}` : "",
  ].filter(Boolean).join(" · ");
}

/** The sentence saying where this comes from and when, for a tooltip. */
export function unpaidTaxesBasis(unit: Taxes): string {
  if (!hasUnpaidTaxes(unit)) return "";
  const when = formatDay(unit.tax_as_of);
  return (
    "As listed as delinquent by the county Tax Collector" +
    (when ? ` on ${when}` : "") +
    ". The amount is the balance the list showed that day."
  );
}
